import { observer } from 'mobx-react-lite';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from '@shared/utils/i18n';
import { OsiActsWidgetViewModel } from '@widgets/osiActs/model/viewModel';
import { PdfViewer } from '@shared/ui/pdfViewer/ui';
import SignerBasic from '@shared/common/SignerBasic';
import { CombinedActItemsTable } from '@widgets/osiActs/ui/combined/CombinedActItemsTable';
import { formatActAmount, formatActPeriod } from '@widgets/osiActs/ui/combined/format';

interface Props {
  viewModel: OsiActsWidgetViewModel;
}

/**
 * OSI-818: диалог подписания общего акта по БИН/ИИН. Устроен как NotSignedActsDialog:
 * PDF общего акта в просмотре и SignerBasic, который подписывает этот PDF целиком
 * и отдаёт CMS в onPostSignCombined. Над PDF — состав, чтобы подписант видел,
 * что подписывает акты всех домов группы, а не только своего.
 */
export const CombinedActDialog: React.FC<Props> = observer(({ viewModel }) => {
  const { combinedActDialog, onCloseCombinedActDialog, onPostSignCombined, currentOsiId } = viewModel;
  const { t } = useTranslation();

  const { combinedAct } = combinedActDialog;
  const housesCount = combinedAct?.items?.length ?? 0;
  // OSI-818: при открытии лента прокручивается к акту текущего дома — ищем его номер
  // (actNum печатается в шапке каждого акта, oci-docs/template/act.html).
  const ownActNum = combinedAct?.items?.find((item) => item.osiId === currentOsiId)?.actNum;

  return (
    // OSI-818: fullWidth вместо minWidth 1200 у содержимого — ширину задаёт диалог,
    // PdfViewer подстраивает страницы под неё, горизонтальной прокрутки нет.
    <Dialog open={combinedActDialog.isOpen} onClose={onCloseCombinedActDialog} maxWidth="lg" fullWidth>
      <DialogTitle>
        {combinedAct &&
          t('acts:combinedAct.dialogTitle', {
            idn: combinedAct.idn,
            period: formatActPeriod(combinedAct.actPeriod),
            count: housesCount
          })}
      </DialogTitle>
      <DialogContent>
        <Box>
          {combinedAct && (
            <Box sx={{ mb: 2 }}>
              <CombinedActItemsTable items={combinedAct.items ?? []} currentOsiId={currentOsiId} />
              <Typography variant="subtitle2" align="right" sx={{ mt: 1 }}>
                {t('acts:combinedAct.totalAmount')}: {formatActAmount(combinedAct.totalAmount)}
              </Typography>
            </Box>
          )}
          {combinedActDialog.doc?.base64 && (
            <PdfViewer
              base64={combinedActDialog.doc.base64}
              scrollToText={ownActNum}
              scrollToLabel={t('acts:combinedAct.scrollToHouseAct')}
              // Над лентой — таблица состава, поэтому область просмотра чуть ниже стандартной.
              height="55vh"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {combinedActDialog.doc?.base64 && (
          <Box
            sx={{
              mt: '4px',
              mr: 2
            }}
          >
            {/* OSI-818: подписывается PDF общего акта целиком — один диалог выбора сертификата,
                готовый CMS уходит в onPostSignCombined. SignerBasic не менялся. */}
            <SignerBasic
              data={combinedActDialog.doc.base64}
              onPostSign={onPostSignCombined}
              content={t('acts:combinedAct.signConsent', { count: housesCount })}
            />
          </Box>
        )}
        <Button variant="outlined" color="inherit" onClick={onCloseCombinedActDialog}>
          {t('common:cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
